/**
 * PSV-1338 — ship upgrade tier table.
 *
 * Linear 5-tier progression purchased with gems (currency earned via
 * PSV-1337 asteroid mining). Each tier replaces the previous one — there
 * is no per-stat slot allocation. A ship at `tier = N` has the stats
 * declared at `UPGRADE_TABLE[N]`.
 *
 * Shared between client and server; both import this single source of
 * truth so cost / stat decisions never drift between the validating
 * authority (server) and the cost-rendering UI (client).
 *
 * Cost curve doubles roughly every tier (50 → 150 → 400 → 1000), gating
 * progression behind sustained mining; stats grow ~50%/tier to keep
 * combat meaningful but not trivializing.
 */

export interface UpgradeTierStats {
  /** 0-indexed tier number. Player records carry this in `upgradeTier`. */
  tier: number;
  /** Gems required to ADVANCE TO this tier (so tier 0 costs 0). */
  cost: number;
  /** Maps to `Player.Ship.HpMax` after the upgrade applies. */
  hpMax: number;
  /** Maps to `Player.Ship.WeaponDmg` after the upgrade applies. */
  weaponDmg: number;
  /** Maps to `Player.Ship.engineMax` — movement speed cap after upgrade. */
  engineMax: number;
}

export const UPGRADE_TABLE: readonly UpgradeTierStats[] = [
  { tier: 0, cost: 0, hpMax: 100, weaponDmg: 10, engineMax: 200 },
  { tier: 1, cost: 50, hpMax: 150, weaponDmg: 15, engineMax: 230 },
  { tier: 2, cost: 150, hpMax: 220, weaponDmg: 22, engineMax: 260 },
  { tier: 3, cost: 400, hpMax: 320, weaponDmg: 32, engineMax: 290 },
  { tier: 4, cost: 1000, hpMax: 460, weaponDmg: 45, engineMax: 320 },
] as const;

/** Highest reachable tier (`UPGRADE_TABLE.length - 1`). */
export const MAX_TIER = UPGRADE_TABLE.length - 1;

/**
 * Lookup helper with bounds clamping — returns the row for the given tier,
 * clamping `tier < 0` or `tier > MAX_TIER` to the boundary so callers
 * never crash on a corrupt/legacy Player record.
 */
export function statsForTier(tier: number): UpgradeTierStats {
  if (tier <= 0) return UPGRADE_TABLE[0]!;
  if (tier >= MAX_TIER) return UPGRADE_TABLE[MAX_TIER]!;
  return UPGRADE_TABLE[tier]!;
}
